import type {CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import type {LocalizedReactNode} from './types';

const WrapperLabel = styled(
  'label',
  {
    alignItems: 'center',
    display: 'flex',
    paddingTop: 0,
    paddingRight: 0,
    paddingBottom: 20,
    paddingLeft: 0,

    ':not(:empty) ~ .Checkbox--WrapperLabel': {
      marginTop: -12,
    },
    ':not(:empty) ~ hr + .Checkbox--WrapperLabel': {
      marginTop: 0,
    },
  },
  {className: 'Checkbox--WrapperLabel'},
);
const InvisibleCheckbox = styled(
  'input',
  (_, designColors) => ({
    border: 0,
    display: 'block',
    flex: '0 0',
    height: 0,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    opacity: 0,
    padding: 0,
    width: 0,

    ':checked ~ .CheckBox-text::before': {
      backgroundColor: designColors.accentBlue,
      borderColor: designColors.accentBlue,
      boxShadow: `0 0 0 0 ${designColors.accentBlueLight}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
    },
    ':checked ~ .CheckBox-text::after': {
      opacity: 1,
      transform: 'scale(1)',
    },
    ':focus ~ .CheckBox-text::before': {
      borderColor: designColors.accentBlueLight,
      boxShadow: `0 0 0 2px ${designColors.accentBlue}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
    },
    ':checked:focus ~ .CheckBox-text::before': {
      boxShadow: `0 0 0 2px ${designColors.accentBlueLight}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
    },

    ':focus': {
      outline: 'none',
    },
  }),
  {type: 'checkbox'},
);

const Text = styled(
  'div',
  (
    {
      $alignInput,
      $disabled,
    }: {
      $alignInput: 'top' | 'center';
      $disabled: boolean;
    },
    designColors,
  ) => ({
    display: 'flex',
    fontFamily: 'Monument Grotesk Bold',
    fontSize: 14,
    fontWeight: 500,
    flex: '1 1',
    opacity: $disabled ? 0.5 : 1,
    paddingLeft: 24,
    position: 'relative',
    transition: 'opacity 0.2s',

    '::before': {
      backgroundColor: '#fff',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: designColors.borderGrayAccent,
      borderRadius: 4,
      bottom: 0,
      boxShadow: `0 0 0 0 ${designColors.accentBlue}, 0 1px 0 rgba(0, 0, 0, 0.1), 0 2px 0 rgba(0, 0, 0, 0.1)`,
      content: '""',
      height: 16,
      left: 0,
      margin: $alignInput === 'top' ? '0 auto auto' : 'auto',
      position: 'absolute',
      top: $alignInput === 'top' ? 1 : 0,
      transition: 'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
      width: 16,
      zIndex: 1,
    },
    '::after': {
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg width='11' height='10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.49 3.17L3.83 8.83 1 6' stroke='%23fff' stroke-width='1.5'/%3E%3C/svg%3E\")",
      // backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      bottom: 2,
      content: '""',
      height: 16,
      left: 0,
      margin: $alignInput === 'top' ? '0 auto auto' : 'auto',
      opacity: 0,
      position: 'absolute',
      top: $alignInput === 'top' ? 1 : 0,
      transform: 'scale(0.75)',
      transition: 'transform 0.2s, opacity 0.2s',
      width: 16,
      zIndex: 2,
    },
  }),
  {className: 'CheckBox-text'},
);

const Checkbox = ({
  alignInput = 'center',
  checked,
  disabled,
  nativeEvents,
  onChange,
  style,
  tabIndex,
  text,
  textStyle,
}: {
  alignInput?: 'top' | 'center';
  checked: boolean;
  disabled?: boolean;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLInputElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLInputElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLInputElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLInputElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLInputElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLInputElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLInputElement>['onKeyPress'];
  }>;
  onChange: (checkedValue: boolean) => void;
  style?: CSSProperties;
  tabIndex?: number;
  text: LocalizedReactNode;
  textStyle?: CSSProperties;
}) => {
  return (
    <WrapperLabel style={style}>
      <InvisibleCheckbox
        checked={checked || false}
        disabled={disabled || false}
        {...nativeEvents}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          onChange(e.target.checked);
          if (nativeEvents && nativeEvents.onChange) {
            nativeEvents.onChange(e);
          }
        }}
        tabIndex={tabIndex}
      />
      <Text
        $alignInput={alignInput}
        $disabled={disabled || false}
        style={textStyle}
      >
        {text}
      </Text>
    </WrapperLabel>
  );
};

export default Checkbox;
